import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * What an item IS, said the same way wherever an item is described - a mission's objects (PathEditor) and the item
 * inventory's editor (ItemEditor): can the player pick it up, can things go in or on it, how its state changes. Plus
 * ItemPicker: a list of items to search by name or what it's for, when a plain dropdown is too long to scan.
 */
export const MAX_STATES = 6;         // a picture each - one sheet (backend traits.MAX_STATES)
export const CARRY = [['carry', 'Picks it up (carried)'], ['push', 'Pushes it (too big to carry)'], ['no', 'Fixed in place']];
export const HOLDS = [['', 'Nothing goes in or on it'], ['in', 'Things go in it'], ['on', 'Things go on it']];
const CARRY_SHORT = { carry: 'Picked up', push: 'Pushed (too big to carry)', no: 'Fixed in place' };
const HOLDS_SHORT = { in: 'things go in it', on: 'things go on it' };

/** "Picked up · things go in it · open / closed" - what an item is, in a line. */
export const whatItIs = (is) => [CARRY_SHORT[is?.carry], HOLDS_SHORT[is?.holds], is?.words?.length === 2 ? is.words.join(' / ') : null]
  .filter(Boolean).join(' · ');

/** A field with its name above it and, optionally, a hint below - so a row of inputs says what each one is. */
export function Labelled({ label, hint, children, wide = false }) {
  return (
    <div className={`gb-ifield ${wide ? 'is-wide' : ''}`}>
      <span className="gb-ifield-label">{label}</span>
      {children}
      {hint && <span className="gb-ifield-hint">{hint}</span>}
    </div>
  );
}

/**
 * Pick one item from a long list by typing: a button that shows the pick, opening a search box over the list (name and
 * what it's for). Arrow keys move, Enter picks, Escape closes. `options`: [{value, label, hint?}].
 */
export function ItemPicker({ value, options, onChange, label, placeholder = 'Search items…' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const box = useRef(null);
  const list = useRef(null);
  const current = options.find((o) => o.value === value);
  const shown = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return options.filter((o) => words.every((w) => `${o.label} ${o.hint || ''} ${o.value}`.toLowerCase().includes(w)));
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  useEffect(() => { list.current?.children[active]?.scrollIntoView?.({ block: 'nearest' }); }, [active]);

  const pick = (o) => { onChange(o.value); setOpen(false); setQuery(''); };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (shown[active]) pick(shown[active]); }
    else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  };

  return (
    <div className="gb-picker" ref={box}>
      <button type="button" className="gb-picker-button" aria-label={label} aria-haspopup="listbox" aria-expanded={open}
        onClick={() => { setOpen((o) => !o); setActive(Math.max(0, options.findIndex((o) => o.value === value))); }}>
        <span>{current ? current.label : value}</span><span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="gb-picker-pop">
          <input type="search" autoFocus role="combobox" aria-expanded="true" aria-controls="gb-picker-list" aria-label={`${label}: search`}
            placeholder={placeholder} value={query} onKeyDown={onKey}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }} />
          <ul className="gb-picker-list" role="listbox" id="gb-picker-list" ref={list} aria-label={label}>
            {shown.map((o, i) => (
              <li key={o.value} role="option" aria-selected={o.value === value} className={i === active ? 'is-active' : ''}
                onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); pick(o); }}>
                <strong>{o.label}</strong>{o.hint && <span className="gb-small gb-muted"> - {o.hint}</span>}
              </li>
            ))}
            {!shown.length && <li className="gb-small gb-muted">Nothing matches “{query}”</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * What a new object IS: how the player moves it, what goes in or on it - and, a holder that opens, when what's inside
 * can be seen. `value`: {carry, holds, states, inside}; `onChange(patch)`.
 */
export function ItemWhat({ name, value, onChange }) {
  const states = value.states || [];
  return (
    <div className="gb-itraits" role="group" aria-label={`What ${name} is`}>
      <Labelled label="The player">
        <select aria-label={`How ${name} moves`} value={value.carry} onChange={(e) => onChange({ carry: e.target.value })}>
          {CARRY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Labelled>
      <Labelled label="Holds">
        <select aria-label={`What ${name} holds`} value={value.holds || ''}
          onChange={(e) => onChange({ holds: e.target.value || undefined, inside: undefined })}>
          {HOLDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Labelled>
      {value.holds === 'in' && states[0] && states[1] && (
        <Labelled label="Inside can be seen">
          <select aria-label={`When what's in ${name} can be seen`} value={value.inside || ''}
            onChange={(e) => onChange({ inside: e.target.value || undefined })}>
            <option value="">always</option>
            {states.map((w) => <option key={w} value={w}>only when {w}</option>)}
          </select>
        </Labelled>
      )}
    </div>
  );
}

/**
 * What an object can be, one row per state with how it looks then (its picture): the first is how it starts. Which it
 * becomes, in what order, with what tool, is each mission's - its end lines (PathEditor). `more`: states can be added
 * (a new object - up to MAX_STATES), else just its two. `looks`: {word: how it looks}, changed by `onLook(word, text)`
 * (none: not drawn by these words).
 */
export function ItemStates({ name, states, looks, more, onStates, onLook }) {
  const set = (k, word) => onStates(states.map((w, j) => (j === k ? word : w)));
  return (
    <div className="gb-ichanges" role="group" aria-label={`States of ${name}`}>
      <div className="gb-ichange gb-istate gb-ichange-head" aria-hidden="true">
        <span />
        <span>State</span>
        <span>{onLook ? 'Looks then (its picture)' : ''}</span>
        <span />
      </div>
      {states.map((word, k) => (
        <div className="gb-ichange gb-istate" key={k}>
          <span className="gb-ichange-n">{k === 0 ? 'Starts as' : 'Can be'}</span>
          <input type="text" className="gb-path-word" aria-label={k === 0 ? `How ${name} starts` : `State ${k + 1} of ${name}`}
            placeholder={k === 0 ? 'e.g. dirty' : 'e.g. clean'} value={word} onChange={(e) => set(k, e.target.value)} />
          {onLook && word.trim() ? (
            <input type="text" className="gb-ichange-look" maxLength={220} aria-label={`How ${name} looks when ${word}`}
              placeholder="how it looks (optional)" value={looks?.[word] || ''} onChange={(e) => onLook(word, e.target.value)} />
          ) : <span />}
          {more && k > 1 ? (
            <button type="button" className="gb-link" aria-label={`Remove state ${word || k + 1} of ${name}`}
              onClick={() => onStates(states.filter((_, j) => j !== k))}>✕</button>
          ) : <span />}
        </div>
      ))}
      <div className="gb-ichange-foot">
        {more && states.length < MAX_STATES && (
          <button type="button" className="gb-link gb-small" onClick={() => onStates([...states, ''])}>+ Add a state</button>
        )}
        <span className="gb-ifield-hint">Leave empty if it doesn&rsquo;t change. What it becomes - and with what - is set under
          How it must end.</span>
      </div>
    </div>
  );
}
